import { useTranslation } from 'react-i18next';
import { MdOutlineWindow, MdZoomInMap, MdZoomOutMap } from 'react-icons/md';
import { RiCrop2Fill } from 'react-icons/ri';
import { useMutation } from 'react-query';
import {
  DeleteOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
  SwapOutlined,
  UndoOutlined,
  ZoomInOutlined,
} from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import {
  Actions,
  Product,
  RotationDirection,
  SessionDto,
  Side,
  UIToolboxButtons,
} from 'shared-types';

import Box from 'components/Box/Box';

import useProductsStore from 'hooks/stores/useProductsStore';
import useSessionStore from 'hooks/stores/useSessionStore';

import API from 'services/API';

type Props = {
  onRotate: (direction: RotationDirection) => void;
  onFlip: () => void;
  onPreview?: () => void;
  onCrop?: () => void;
  onZoomOut?: () => void;
  onSetOriginal?: () => void;
  onSetToFit?: () => void;
  onUndo?: () => void;
  productId: Product['id'];
  sideId: Side['id'];
  showActions?: Actions[];
  disableActions?: Actions[];
};

const actionStyle = {
  cursor: 'pointer',
  alignSelf: 'end',
  marginLeft: 10,
};

const Toolbox = ({
  onRotate,
  onFlip,
  onPreview,
  onCrop,
  onZoomOut,
  onSetOriginal,
  onSetToFit,
  onUndo,
  productId,
  sideId,
  showActions = [
    'rotate',
    'flip',
    'preview',
    'delete',
    'zoom',
    'crop',
    'zoomout',
    'reset',
    'undo',
    'set-to-fit',
  ],
  disableActions,
}: Props) => {
  const { t } = useTranslation();
  const removeAssetFromSide = useProductsStore((state) => state.removeAssetFromSide);
  const session = useSessionStore((state) => state.session);

  // Get UI toolbox configuration from session options
  const uiToolbox = session?.options?.ui?.toolbox?.buttons;

  // Helper function to check if a toolbox button should be shown
  const shouldShowToolboxButton = (action: Actions): boolean => {
    if (!uiToolbox) return showActions.includes(action); // Fallback to original behavior

    const toolboxMap: Record<Actions, keyof UIToolboxButtons> = {
      rotate: 'rotateLeft', // We'll check both rotateLeft and rotateRight for rotate
      flip: 'flip',
      crop: 'crop',
      zoomout: 'zoomOut',
      reset: 'reset',
      undo: 'undo',
      'set-to-fit': 'setToFit',
      zoom: 'undo', // zoom is not used anymore, fallback
      preview: 'undo', // preview is not used anymore, fallback
      delete: 'undo', // This shouldn't be used anymore but keeping for safety
    };

    const buttonKey = toolboxMap[action];
    return buttonKey ? uiToolbox[buttonKey] : showActions.includes(action);
  };
  const useRemoveAssetFromSideMutation = useMutation(
    'removeAssetFromSide',
    (variables: { productId: string; sideId: Side['id'] }) =>
      API.removeAssetFromSide(variables.productId, variables.sideId),
    {
      onError: () => {
        // queryClient.invalidateQueries('products');
      },
      onSuccess: () => {
        // queryClient.invalidateQueries('products');
      },
    },
  );

  const handleRemoveAssetFromSide = () => {
    removeAssetFromSide(productId, sideId);
    useRemoveAssetFromSideMutation.mutate({ productId, sideId });
  };

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      marginY="1rem"
      style={{ backgroundColor: '#f0f2f5', padding: '0.5rem', borderRadius: '4px' }}
    >
      {showActions?.includes('delete') && (
        <Tooltip placement="top" title={t('SideActions.DeleteImage')}>
          <Button
            type="default"
            danger
            onClick={handleRemoveAssetFromSide}
            disabled={disableActions?.includes('delete')}
            icon={<DeleteOutlined />}
          />
        </Tooltip>
      )}

      <Box display="flex" justifyContent={'space-between'} alignItems="center">
        {shouldShowToolboxButton('undo') && (
          <Tooltip placement="top" title={t('SideActions.Undo')}>
            <Button
              type="default"
              style={actionStyle}
              onClick={onUndo}
              disabled={disableActions?.includes('undo')}
              icon={<UndoOutlined />}
            />
          </Tooltip>
        )}
        {shouldShowToolboxButton('rotate') && uiToolbox?.rotateRight !== false && (
          <Tooltip placement="top" title={t('SideActions.RotateRight')}>
            <Button
              type="default"
              style={actionStyle}
              onClick={() => onRotate('left')}
              disabled={disableActions?.includes('rotate')}
              icon={<RotateRightOutlined />}
            />
          </Tooltip>
        )}
        {shouldShowToolboxButton('rotate') && uiToolbox?.rotateLeft !== false && (
          <Tooltip placement="top" title={t('SideActions.RotateLeft')}>
            <Button
              type="default"
              style={actionStyle}
              onClick={() => onRotate('right')}
              disabled={disableActions?.includes('rotate')}
              icon={<RotateLeftOutlined />}
            />
          </Tooltip>
        )}
        {shouldShowToolboxButton('flip') && (
          <Tooltip placement="top" title={t('SideActions.FlipHorizontal')}>
            <Button
              type="default"
              style={actionStyle}
              onClick={onFlip}
              disabled={disableActions?.includes('flip')}
              icon={<SwapOutlined />}
            />
          </Tooltip>
        )}

        {shouldShowToolboxButton('reset') && (
          <Tooltip placement="top" title={t('SideActions.ResetToCrop')}>
            <Button
              type="default"
              style={actionStyle}
              onClick={onSetOriginal}
              icon={<MdOutlineWindow />}
            />
          </Tooltip>
        )}
        {shouldShowToolboxButton('zoomout') && (
          <Tooltip placement="top" title={t('SideActions.ZoomOut')}>
            <Button
              type="default"
              style={actionStyle}
              onClick={onZoomOut}
              icon={<MdZoomOutMap />}
            />
          </Tooltip>
        )}
        {shouldShowToolboxButton('set-to-fit') && (
          <Tooltip placement="top" title={t('SideActions.SetToFit')}>
            <Button
              type="default"
              style={actionStyle}
              disabled={disableActions?.includes('set-to-fit')}
              onClick={onSetToFit}
              icon={<MdZoomInMap />}
            />
          </Tooltip>
        )}
        {shouldShowToolboxButton('crop') && (
          <Tooltip placement="top" title={t('SideActions.Crop')}>
            <Button
              type="default"
              style={actionStyle}
              disabled={disableActions?.includes('crop')}
              onClick={onCrop}
              icon={<RiCrop2Fill />}
            />
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default Toolbox;
